import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { scrollToId } from "../data";
import { useLang } from "../i18n";

export const Navbar = () => {
  const { lang, setLang, s } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: s.nav.rooms, href: "#rooms" },
    { label: s.nav.explore, href: "#explore" },
    { label: s.nav.food, href: "#food" },
    { label: s.nav.book, href: "#booking" },
    { label: s.nav.platforms, href: "#platforms" },
    { label: s.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    fn();
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          scrolled
            ? "bg-cream/90 backdrop-blur-xl shadow-[0_1px_0_rgba(92,26,27,0.12)]"
            : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <button
            onClick={() => go("#top")}
            className="flex items-center"
            data-testid="nav-home-link"
            aria-label="Go to top"
          >
            <span className="flex items-center rounded-xl bg-cream px-2.5 py-1.5 shadow-md ring-1 ring-gold/60">
              <img
                src="/images/logo.png"
                alt="Shri Jamuna Dham Guest House — Vrindavan"
                className="h-10 sm:h-12 w-auto"
                data-testid="brand-logo"
              />
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-medium tracking-wide relative group ${
                  scrolled ? "text-ink" : "text-cream"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-saffron transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              data-testid="language-toggle-button"
              className={`ml-2 flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold tracking-wide transition-colors duration-300 ${
                scrolled
                  ? "border-maroon/25 text-maroon hover:border-saffron"
                  : "border-cream/40 text-cream hover:border-saffron hover:text-saffron"
              }`}
              aria-label="Switch language"
            >
              <Languages size={14} /> {lang === "en" ? "हिंदी" : "EN"}
            </button>
            <button
              onClick={() => go("#booking")}
              data-testid="nav-book-now-button"
              className="ml-2 rounded-full bg-saffron px-6 py-2.5 text-sm font-semibold text-night transition-colors duration-300 hover:bg-gold"
            >
              {s.nav.bookNow}
            </button>
          </nav>

          <button
            className={`lg:hidden p-2 ${scrolled ? "text-ink" : "text-cream"}`}
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-night/95 backdrop-blur-md flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-[72px]">
              <span className="flex items-center rounded-xl bg-cream px-2.5 py-1.5 ring-1 ring-gold/60">
                <img
                  src="/images/logo.png"
                  alt="Shri Jamuna Dham Guest House — Vrindavan"
                  className="h-10 w-auto"
                  data-testid="brand-logo-mobile"
                />
              </span>
              <button
                onClick={() => setOpen(false)}
                className="text-cream p-2"
                data-testid="nav-mobile-close-button"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-start justify-center gap-7 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  onClick={() => go(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="font-display text-4xl text-cream hover:text-saffron transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                onClick={() => setLang(lang === "en" ? "hi" : "en")}
                data-testid="language-toggle-mobile-button"
                className="mt-4 flex items-center gap-2 rounded-full border border-cream/30 px-6 py-3 text-sm font-bold text-cream"
              >
                <Languages size={16} /> {lang === "en" ? "हिंदी में देखें" : "View in English"}
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
